import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DATES_BOOKING_STEPS_STATE } from '@ibe.components/booking/booking.setup';
import { BookingType } from '@ibe.models/booked-trip.model.lib';
import { Airport, PassengersSegmentedCount, BookedTrip } from '@ibe/models';
import { BookingStep } from '@ibe/models/ui';
import { TAPDataStorageService } from '@ibe/services/mocks.tap';
import * as BookingStepsActions from '@ibe/store/actions/booking-steps.actions';
import * as BookedTripActions from '@ibe/store/actions/booked-trip.actions';
import * as StopoverHotelsAction from '@ibe/store/actions/stopover-hotels.actions';
import { Store } from '@ngrx/store';
import * as fromStore from '../store';
import * as moment from 'moment';
import { environment } from '@environments/environment';

type BookingSearch = {
	flights?: { to: Airport; from: Airport; date: any }[];
	stopovers?: [{ departure: any, arrival: any, country: any, city: any, when: any, days: any, id?: any }];
	to: Airport;
	from: Airport;
	routeType: BookingType;
	passengers: PassengersSegmentedCount;
};

@Injectable()
export class BookingStepsEffect {

	constructor(
		private store: Store<fromStore.State>,
		private router: Router,
		private storage: TAPDataStorageService,
	) {
		this.store.dispatch(new BookingStepsActions.LoadingAction);
		this.storage.initializeBookingsIfEmpty();
		moment.locale(environment.locale);
	}

	/**
	 * Create booking steps based on formValue
	 * Create booking Trip empty
	 * @param formValue
	 */
	async createBookingSteps(formValue: BookingSearch, discounts: any = null, dates: any[] = null) {
		return new Promise(async (resolve, reject) => {

			// const bookedTrip = await this.storage.readActiveBookingInitializerFile();
			const bookedTrip: BookedTrip = new BookedTrip([], 'PNRXXX', 'R'); // initial and temporary values

			let steps = [];
			switch (formValue.routeType) {
				case 'R':
					
					if (formValue.stopovers && formValue.stopovers.length) {
						if (formValue.stopovers.length > 2) {
							formValue.stopovers.splice(0, 1);
						}
						const stopover = { inbound: null, outbound: null };

						const inbound = formValue.stopovers.find(cur => cur.when == 'inbound');
						const outbound = formValue.stopovers.find(cur => cur.when == 'outbound');

						if (inbound) stopover.inbound = { days: inbound.days, airport: inbound.city, when: "inbound" };
						if (outbound) stopover.outbound = { days: outbound.days, airport: outbound.city, when: "outbound" };

						steps = [];
						if (outbound) {
							steps.push(new BookingStep(formValue.from, stopover.outbound.airport, moment(formValue.from.date).toDate(), stopover.outbound));
							steps.push(new BookingStep(stopover.outbound.airport, formValue.to, moment(formValue.from.date).add(stopover.outbound.days, 'days').toDate()));
							if (!inbound) steps.push(new BookingStep(formValue.to, formValue.from, moment(formValue.to.date).toDate(), stopover.inbound));
						}

						if (inbound) {
							if (!outbound) steps.push(new BookingStep(formValue.from, formValue.to, moment(formValue.from.date).toDate(), stopover.outbound));
							steps.push(new BookingStep(formValue.to, stopover.inbound.airport, moment(formValue.to.date).toDate(), stopover.inbound));
							steps.push(new BookingStep(stopover.inbound.airport, formValue.from, moment(formValue.to.date).add(stopover.inbound.days, 'days').toDate()));
						}

						bookedTrip.setStopover(true);

					} else {
						if (dates && dates.length > 0) {
							steps = [new BookingStep(formValue.from, formValue.to, dates[0]), new BookingStep(formValue.to, formValue.from, dates[1])];
						} else {
							steps = [new BookingStep(formValue.from, formValue.to), new BookingStep(formValue.to, formValue.from)];
						}
					}

					break;
				case 'O':

					if (formValue.stopovers && formValue.stopovers.length) {

						const outbound = formValue.stopovers.find(cur => cur.when == 'outbound');

						steps = [
							new BookingStep(formValue.from, 
											outbound.city, 
											moment(formValue.from.date).toDate(), 
											{ days: outbound.days, airport: outbound.city, when: "outbound" }),
											
							new BookingStep(outbound.city, 
											formValue.to, 
											moment(formValue.from.date).add(outbound.days, 'days').toDate()),
						];
						bookedTrip.setStopover(true);

					} else {
						if (dates && dates.length > 0) {
							steps = [new BookingStep(formValue.from, formValue.to, dates[0])];
						} else {
							steps = [new BookingStep(formValue.from, formValue.to, null)];
						}
					}

					break;
				case 'M':

					if (formValue.stopovers && formValue.stopovers.length) {

						const outbound = formValue.stopovers.find(cur => cur.when && cur.when.includes('outbound'));
						const stopover = { inbound: null, outbound: null };
						if (outbound) stopover.outbound = { days: outbound.days, airport: outbound.city, when: outbound.when };
						
						steps = formValue.flights.reduce((acc, flight, idx) => {

							const ckStopover = formValue.stopovers.find(c => c.when && c.when.includes(`${flight.from.code}-${flight.to.code}`));

							if (ckStopover) {
								if (idx == 0) {
									acc.push(new BookingStep(flight.from, stopover.outbound.airport, moment(`${flight.date.date.year}-${flight.date.date.month}-${flight.date.date.day}`, 'YYYY-M-D').toDate(), stopover.outbound));
									acc.push(new BookingStep(stopover.outbound.airport, flight.to, moment(flight.from.date).add(stopover.outbound.days, 'days').toDate()));
								} else {
									acc.push(new BookingStep(flight.from, stopover.outbound.airport, moment(`${flight.date.date.year}-${flight.date.date.month}-${flight.date.date.day}`, 'YYYY-M-D').toDate(), stopover.outbound));
									acc.push(new BookingStep(stopover.outbound.airport, flight.to, moment(`${flight.date.date.year}-${flight.date.date.month}-${flight.date.date.day}`).add(stopover.outbound.days, 'days').toDate()));
								}
							} else {
								acc.push(new BookingStep(flight.from, flight.to, moment(`${flight.date.date.year}-${flight.date.date.month}-${flight.date.date.day}`, 'YYYY-M-D').toDate()));
							}
							return acc;

						}, []);

						bookedTrip.setStopover(true);

					} else {
						steps = formValue.flights.map(cur => 
							new BookingStep(
								cur.from, 
								cur.to, 
								(cur.date ? moment(`${cur.date.date.year}-${cur.date.date.month}-${cur.date.date.day}`, 'YYYY-M-D').toDate() : null)));
					}

					break;
				default:
					steps = DATES_BOOKING_STEPS_STATE;
					break;

			}

			const payload = {
				current_step: 0,
				booking_steps: steps,
				booking_type: formValue.routeType
			};


			const passengers: PassengersSegmentedCount = formValue.passengers;
			bookedTrip.passengers = passengers;

			bookedTrip.emptyPassengers();
			bookedTrip.emptyFlights();

			if (passengers.adults && passengers.adults > 0) {
				for (let i = 0; i < passengers.adults; i++) {
					bookedTrip.addPassenger('adult');
				}
			}

			if (passengers.adults && passengers.youngAdults > 0) {
				for (let i = 0; i < passengers.youngAdults; i++) {
					bookedTrip.addPassenger('youngAdult');
				}
			}

			if (passengers.children && passengers.children > 0) {
				for (let i = 0; i < passengers.children; i++) {
					bookedTrip.addPassenger('child');
				}
			}

			if (passengers.infants && passengers.infants > 0) {
				for (let i = 0; i < passengers.infants; i++) {
					bookedTrip.addPassenger('infant');
				}
			}

			bookedTrip.setType(formValue.routeType);
			
			if (discounts)
				bookedTrip.discounts = discounts;

			this.store.dispatch(new BookingStepsActions.PutBookingStepsAction(payload));
			this.store.dispatch(new BookedTripActions.PutBookedTripAction(bookedTrip));

			resolve();
		});

	}
	getCostsPerDay(totalSteps: number) {
		const allCostsPerDay = [];
		for (let i = 0; i < totalSteps; i++) {
			allCostsPerDay.push(this.storage.generateMockCosts());
		}
		this.store.dispatch(new BookingStepsActions.PutAllCostsPerDayAction(allCostsPerDay));
	}

	cancelBookingSteps() {
		this.store.dispatch(new BookingStepsActions.PutCurrentPageAction(null));
		this.store.dispatch(new BookingStepsActions.ResetAction());
	}

	selectNewDates() {
		this.store.dispatch(new BookingStepsActions.ResetSelectedFlightAction);
		this.store.dispatch(new BookingStepsActions.PutBookingStepsAction(0));
	}
}
